import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, extractErrorMessage } from '../api/client';
import { useToast } from '../components/Toast';
import { useApi } from '../hooks/useApi';
import { useAuth } from '../hooks/useAuth';
import { diaADia } from '../lib/diaADia';
import { filterNavItems } from '../lib/filterNavItems';
import { Button } from '../components/Button';
import { HelpTip } from '../components/HelpTip';
import { Skeleton } from '../components/Skeleton';
import { VerInstructivo } from '../components/VerInstructivo';

/**
 * Sección de GET /api/v3/hoy: sólo conteos; el servidor manda únicamente las de las pantallas que el usuario puede abrir,
 * ya ordenadas (urgentes, resto, las que no se pudieron contar). Acá no se reordena.
 */
export interface SeccionHoy {
  clave: string;
  titulo: string;
  /** null si no se pudo contar (error: true). */
  cantidad: number | null;
  enlace: string;
  nivel: 'urgente' | 'normal';
  /** AAAA-MM-DD del caso más viejo. */
  masAntiguo: string | null;
  /** Aclaración del renglón ("Incluye las 2 atrasadas") o por qué no se pudo contar. */
  detalle: string | null;
  error: boolean;
  /** Botón del renglón (sólo «ml-ventas-sin-aviso»); permiso con forma 'modulo:accion'. */
  accion?: { texto: string; endpoint: string; permiso: string } | null;
}

/** AAAA-MM-DD → DD/MM/AAAA, sin pasar por Date (evita el corrimiento de un día por la zona horaria). */
const fechaCorta = (iso: string) => iso.slice(0, 10).split('-').reverse().join('/');


interface ResumenBusqueda { ok: boolean; error: string | null; revisadas: number; faltantes: number; cancelacionesPerdidas: number; pendientes: number }

/** Resultado de «Buscar ventas ahora» para el toast de éxito. */
function textoBusqueda({ revisadas, faltantes, cancelacionesPerdidas: canc, pendientes }: ResumenBusqueda): string {
  if (faltantes + canc + pendientes === 0) return 'No faltaba ninguna venta.';
  const buscadas = revisadas === 1 ? 'Se buscó 1 venta' : `Se buscaron ${revisadas} ventas`;
  const traidas = faltantes === 0 ? 'no faltaba ninguna' : faltantes === 1 ? '1 faltaba y se está trayendo' : `${faltantes} faltaban y se están trayendo`;
  return `${buscadas}: ${traidas}.`
    + (canc > 0 ? ` También se están corrigiendo ${canc === 1 ? '1 cancelación' : `${canc} cancelaciones`}.` : '')
    + (pendientes > 0 ? ` ${pendientes === 1 ? 'Queda 1' : `Quedan ${pendientes}`} para la próxima vuelta.` : '');
}

function BotonAccion({ accion, onHecho }: { accion: NonNullable<SeccionHoy['accion']>; onHecho: () => void }) {
  const { can } = useAuth();
  const { addToast } = useToast();
  const [enviando, setEnviando] = useState(false);
  const enCurso = useRef(false); // el estado no alcanza: dos clics en el mismo tick leen el mismo valor
  const [modulo, permiso] = accion.permiso.split(':');
  if (!can(modulo, permiso)) return null;

  const buscar = async () => {
    if (enCurso.current) return;
    enCurso.current = true;
    setEnviando(true);
    try {
      const r = await api.post<ResumenBusqueda>(accion.endpoint, {});
      if (!r.ok) addToast(r.error ?? 'No se pudo buscar.', 'error');
      else addToast(textoBusqueda(r), 'success');
      onHecho();
    } catch (e) {
      addToast(extractErrorMessage(e), 'error');
    } finally {
      enCurso.current = false;
      setEnviando(false);
    }
  };

  return (
    <Button size="sm" loading={enviando} onClick={() => void buscar()}>
      {accion.texto}
    </Button>
  );
}

function Renglon({ seccion, onHecho }: { seccion: SeccionHoy; onHecho: () => void }) {
  const urgente = !seccion.error && seccion.nivel === 'urgente';
  return (
    <li className={seccion.accion ? 'flex flex-col hover:bg-slate-50 sm:flex-row sm:items-center sm:pr-4' : undefined}>
      <Link
        to={seccion.enlace}
        className={`group flex items-center gap-4 px-4 py-3 hover:bg-slate-50 focus-visible:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-500${seccion.accion ? ' min-w-0 flex-1' : ''}`}
      >
        <span
          className={`w-12 shrink-0 text-right text-2xl font-bold tabular-nums ${urgente ? 'text-red-700' : seccion.error ? 'text-slate-400' : 'text-slate-800'}`}
        >
          {seccion.error ? '—' : seccion.cantidad}
        </span>
        <span className="min-w-0 flex-1">
          <span className="block font-medium text-slate-800 group-hover:text-brand-700">{seccion.titulo}</span>
          {seccion.error ? (
            <span className="block text-sm text-slate-500">{seccion.detalle ?? 'No se pudo contar'}. Probá Actualizar o entrá a la pantalla.</span>
          ) : (
            <>
              {seccion.masAntiguo && (
                <span className={`block text-sm ${urgente ? 'text-red-700' : 'text-slate-500'}`}>El más antiguo: {fechaCorta(seccion.masAntiguo)}</span>
              )}
              {seccion.detalle && <span className="block text-sm text-slate-500">{seccion.detalle}</span>}
            </>
          )}
        </span>
        <svg className="h-5 w-5 shrink-0 text-slate-400 group-hover:text-brand-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
        </svg>
      </Link>
      {/* En el celular el botón va debajo del texto (alineado con el título); desde sm, a la derecha. */}
      {seccion.accion && <div className="pb-3 pl-20 pr-4 sm:p-0"><BotonAccion accion={seccion.accion} onHecho={onHecho} /></div>}
    </li>
  );
}

/**
 * Lo que hace un operador de depósito en el ERP (recibir, contar y bajas/ajustes): un botón
 * por tarea que abre la pantalla con la ventana ya abierta. Cada uno pide ver la pantalla y poder hacer lo que abre; el admin no lo ve.
 */
const TAREAS = [
  { texto: 'Recibir mercadería', ayuda: 'Cuando llega mercadería de una orden de compra.', enlace: '/remitos?pendientes=1', permisos: ['remitos:ver', 'remitos:crear'], pantalla: 'remitos' },
  { texto: 'Contar stock', ayuda: 'Para contar lo que hay en una ubicación.', enlace: '/conteos-inventario', permisos: ['wms:ver'], pantalla: 'conteos-de-inventario' },
  { texto: 'Sacar o sumar stock', ayuda: 'Para dar de baja o sumar unidades a mano.', enlace: '/movimientos-stock?registrar=1', permisos: ['wms:ver', 'wms:editar'], pantalla: 'movimientos-de-stock' },
] as const;
const iconoDe = (slug: string) => diaADia.flatMap((b) => b.items).find((p) => p.slug === slug)?.icon;

function Tareas() {
  const { user, can } = useAuth();
  const tareas = TAREAS.filter((t) => t.permisos.every((p) => can(...(p.split(':') as [string, string]))));
  if (user?.role === 'admin' || tareas.length === 0) return null;
  return (
    <nav aria-label="Qué querés hacer">
      <ul className="grid gap-4 sm:grid-cols-3 sm:gap-3">
        {tareas.map((t) => (
          <li key={t.enlace} className="space-y-1.5">
            <Link
              to={t.enlace}
              className="flex min-h-[3.5rem] w-full items-center justify-center gap-2 rounded-lg bg-brand-600 px-4 py-3 text-base font-semibold text-white shadow-sm transition-colors hover:bg-brand-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2"
            >
              <svg className="h-5 w-5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" d={iconoDe(t.pantalla)} />
              </svg>
              {t.texto}
            </Link>
            <p className="px-1 text-sm text-slate-600">{t.ayuda}</p>
          </li>
        ))}
      </ul>
    </nav>
  );
}

/** Si "Hoy" no carga, la entrada no queda vacía: las pantallas de Día a día que el usuario puede abrir. */
function Atajos() {
  const { permisos } = useAuth();
  const pantallas = filterNavItems(diaADia.flatMap((b) => b.items), permisos);
  if (pantallas.length === 0) return null;
  return (
    <nav aria-label="Ir a una pantalla" className="text-sm text-slate-600">
      <p className="mb-2">Mientras tanto, podés ir directo a:</p>
      <ul className="flex flex-wrap gap-2">
        {pantallas.map((p) => (
          <li key={p.path}>
            <Link to={p.path} className="inline-flex min-h-[2.5rem] items-center rounded-lg border border-slate-300 bg-white px-3 font-medium text-slate-700 hover:bg-slate-50 hover:text-brand-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500">
              {p.label}
            </Link>
          </li>
        ))}
      </ul>
    </nav>
  );
}

export function HoyPage() {
  const { data, loading, error, refetch } = useApi<{ secciones: SeccionHoy[] }>('/api/v3/hoy');
  const secciones = data?.secciones ?? [];
  const paraAtender = secciones.filter((x) => x.error || (x.cantidad ?? 0) > 0);
  const alDia = secciones.filter((x) => !x.error && x.cantidad === 0);

  return (
    <div className="max-w-3xl space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 inline">Hoy</h1>
          <HelpTip
            tips={[
              'Lo que está esperando que alguien lo resuelva. Tocá un renglón para ir a la pantalla donde se resuelve.',
              'Lo urgente va primero: remitos que no entraron al depósito, cuotas vencidas y órdenes de compra atrasadas.',
              'Cada uno ve sólo lo de las pantallas que puede abrir. Los números se cuentan al entrar o al tocar Actualizar.',
            ]}
          />
          <VerInstructivo seccion="hoy" />
          <p className="text-slate-500 text-sm mt-1">Lo que hay que atender, con un enlace a cada pantalla</p>
        </div>
        <Button variant="ghost" size="sm" onClick={() => refetch()} loading={loading && data !== null}>
          Actualizar
        </Button>
      </div>

      <Tareas />

      {error ? (
        <div className="space-y-4">
          <p role="alert" className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
            No se pudo cargar lo de hoy: {error}. Probá Actualizar.
          </p>
          <Atajos />
        </div>
      ) : loading && data === null ? (
        <div role="status" className="rounded-lg border border-slate-200 bg-white divide-y divide-slate-100">
          <span className="sr-only">Cargando lo de hoy…</span>
          {[0, 1, 2].map((i) => (
            <div key={i} className="flex items-center gap-4 px-4 py-4">
              <Skeleton className="h-7 w-12" />
              <Skeleton className="h-4 flex-1" />
            </div>
          ))}
        </div>
      ) : secciones.length === 0 ? (
        <p className="rounded-lg border border-slate-200 bg-white px-4 py-6 text-sm text-slate-600">
          No hay nada para mostrarte acá con tus permisos. Usá el menú para entrar a tus pantallas.
        </p>
      ) : (
        <>
          {paraAtender.length > 0 ? (
            <ul aria-label="Para atender" className="rounded-lg border border-slate-200 bg-white divide-y divide-slate-100 overflow-hidden">
              {paraAtender.map((x) => <Renglon key={x.clave} seccion={x} onHecho={() => refetch()} />)}
            </ul>
          ) : (
            <p className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-6 text-base font-medium text-emerald-800">Todo al día.</p>
          )}
          {alDia.length > 0 && (
            <p className="text-sm text-slate-500">
              <span className="font-medium text-slate-600">Al día:</span>{' '}
              {alDia.map((x, i) => (
                <span key={x.clave}>
                  {i > 0 && ' · '}
                  <Link to={x.enlace} className="underline-offset-2 hover:text-brand-700 hover:underline">{x.titulo}</Link>
                </span>
              ))}
            </p>
          )}
        </>
      )}
    </div>
  );
}
